import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import FormSubmissionBanner from '@/assets/image/modals/form-submission.jpg';
import { X } from 'lucide-react';

import { useCityData, useCountryData, useStateData } from '@/lib/api/apiData';
import { useEntityData } from '@/hooks/useEntityData';

import CityAutocomplete from './InputCityBox';
import AutocompleteCountry from './InputCountryBox';
import AutoCompleteCourse from './InputCourseBox';
import LeadFormReview from './LeadFormReview';
import ServiceContainer from './ServiceContainer';
import useLeadFormModalHook from './useLeadFormModalHook';

const LeadFormModal = ({ isOpen, setIsOpen, isStudyAbroad }: any) => {
  const {
    handleClose,
    submitted,
    handleSubmit,
    handleInputChange,
    formData,
    setFormData,
    otpSent,
    setOtp,
    errorMessage,
    otp,
    title,
    showResend,
    resendOtp,
    timer,
    intakeYears,
    source,
    isAbroadCountry,
    handleToggleCourse,
    course,
    loading,
    modalFields,
  } = useLeadFormModalHook({ setIsOpen, isStudyAbroad });

  const [shouldDataFetch, setShouldDataFetch] = useState(false);
  const { studyAbroadData } = useCountryData(shouldDataFetch);
  const { citiesData } = useCityData(shouldDataFetch);
  const { statesData } = useStateData(shouldDataFetch);

  const courseQueryParams = useMemo(
    () => ({
      sort: 'display_sequence:asc',
      fields: ['course_name', 'abbreviation'],
      publicationState: 'live',
      pagination: {
        page: 1,
        pageSize: 600,
      },
    }),
    [],
  );

  const { data: courseData }: any = useEntityData('courses', courseQueryParams, shouldDataFetch);

  const filteredTopCountryData = studyAbroadData?.filter(
    (country: any) => !country?.country_name?.toLowerCase().includes('india'),
  );

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setShouldDataFetch(true);
    }
  }, [isOpen]);

  const submitButtonText = otpSent ? 'Verify OTP' : 'Submit';
  const collegeReviewTitle = source === 'college-review' ? `Review Now for ${title}!` : `Register Now for ${title}!`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] bg-black bg-opacity-40 flex items-center justify-center">
      <div
        // className="bg-white w-full h-full lg:h-auto lg:w-[70rem] max-h-[95vh] rounded-md shadow-lg overflow-hidden relative"
        className="bg-white lg:max-w-screen-md lg:top-[50%] inset-y-0 top-[50%] w-full lg:min-w-[70rem] lg:min-h-80 h-full max-h-[48rem] lg:max-h-[38rem] outline-none shadow-lg drop-shadow "
      >
        {!submitted ? (
          <div className="flex w-full h-full">
            <ServiceContainer />
            <div className="lg:basis-3/5 w-full h-full flex flex-col gap-x-4 px-4 lg:px-7 py-4 relative overflow-y-auto">
              <div className="lg:mt-5 font-poppins pr-12 lg:pr-16">
                <h2 className="font-bold text-lg lg:text-2xl text-primary leading-tight truncate max-w-full">
                  {title ? collegeReviewTitle : 'Register Now!'}
                </h2>
                <p className="text-secondary-gray text-xs lg:text-sm px-1 truncate">
                  Get Free Counselling & Regular Updates
                </p>
              </div>

              <button
                className="absolute right-3 top-4 lg:right-5 lg:top-5 cursor-pointer text-lg font-bold"
                onClick={handleClose}
              >
                <X className="size-5" />
              </button>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                {modalFields?.map((field: any, index: any) => {
                  if (field.name === 'course') {
                    return (
                      <div key={field.name} className="space-y-1">
                        <AutoCompleteCourse
                          formData={formData}
                          setFormData={setFormData}
                          courseData={courseData}
                          field={field}
                          disabled={otpSent}
                          course={course}
                        />
                      </div>
                    );
                  } else if (field.name === 'city') {
                    return (
                      <div key={field.name}>
                        <CityAutocomplete
                          formData={formData}
                          setFormData={setFormData}
                          field={field}
                          disabled={otpSent}
                          citiesData={citiesData}
                          statesData={statesData}
                        />
                      </div>
                    );
                  } else if (field.name === 'country') {
                    if (isStudyAbroad || isAbroadCountry) {
                      return (
                        <AutocompleteCountry
                          key={field.name}
                          field={field}
                          formData={formData}
                          filteredTopCountryData={filteredTopCountryData}
                          setFormData={setFormData}
                          intakeYears={intakeYears}
                          disabled={otpSent}
                          source={source}
                        />
                      );
                    }
                    return null;
                  } else {
                    return (
                      <div key={field.name} className="space-y-2">
                        <div className="font-medium text-xs lg:text-sm text-[#404D61] px-1 flex items-center gap-x-1">
                          {field?.icon}
                          <label className="text-sm lg:text-base">{field.label}</label>
                        </div>
                        <input
                          name={field.name}
                          type={field.type}
                          onChange={handleInputChange}
                          onInput={(e: any) => {
                            if (field.name === 'phone_number') {
                              e.target.value = e.target.value.replace(/\D/g, '');
                            }
                          }}
                          maxLength={field.name === 'phone_number' ? 10 : undefined}
                          value={formData[field.name]}
                          className="w-full px-4 py-1.5 text-gray-700 placeholder:text-secondary-gray border text-xs lg:text-sm border-[#E1E3E6] rounded-md outline-none"
                          placeholder={field.placeholder}
                          disabled={otpSent}
                          required
                        />
                      </div>
                    );
                  }
                })}

                <div className="flex items-center gap-x-2 lg:pl-4 lg:mt-3">
                  <label className="text-sm lg:text-base font-semibold text-primary">
                    Are you looking for Online courses?
                  </label>
                  <button
                    type="button"
                    onClick={handleToggleCourse}
                    className={`w-10 h-5 rounded-full relative transition duration-300 ${formData.is_online_intrested ? 'bg-blue-600' : 'bg-gray-300'}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-300 ${
                        formData.is_online_intrested ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>

                {otpSent && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm lg:text-base">Enter OTP</label>
                      <input
                        type="tel"
                        onChange={(e) => setOtp(e.target.value)}
                        value={otp}
                        onInput={(e: any) => {
                          e.target.value = e.target.value.replace(/\D/g, '');
                        }}
                        maxLength={6}
                        className="w-full px-4 py-1.5 text-gray-600 placeholder:text-[#C9C9C9] border text-xs lg:text-sm border-[#E1E3E6] rounded-md outline-none"
                        placeholder="Enter the OTP sent to your phone"
                        required
                      />
                    </div>
                  </>
                )}

                <div className={`flex items-end ${otpSent ? 'justify-start' : 'justify-center lg:col-span-2 lg:mt-2'}`}>
                  <button
                    type="submit"
                    className="bg-primary text-white px-4 py-1.5 rounded-md w-4/5 lg:w-44"
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : submitButtonText}
                  </button>
                </div>

                {otpSent && (
                  <div className="space-y-2">
                    {showResend ? (
                      <button type="button" onClick={resendOtp} className="text-start text-primary-gray">
                        Didn't get OTP? <span className="text-blue-500 underline">Resend OTP</span>
                      </button>
                    ) : (
                      <p className="text-sm">Resend OTP in {timer} seconds</p>
                    )}
                  </div>
                )}
              </form>

              <small className="text-xxs font-sans font-medium mt-5">
                By submitting this form, you accept and agree to the{' '}
                <Link href="/terms-and-condition" onClick={handleClose}>
                  <strong>terms and conditions</strong>
                </Link>
                .
              </small>

              {errorMessage && <p className="text-sm font-inter text-red-500 font-medium mt-5">{errorMessage}</p>}

              <div className="lg:hidden mt-5">
                <LeadFormReview />
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-center p-6 flex-col gap-3">
            <div className="w-40 lg:w-60">
              <Image src={FormSubmissionBanner} alt="Form Submission" />
            </div>
            <h4 className="text-2xl font-poppins font-bold">Your form submitted successfully!</h4>
            <p className="text-xl font-sans">Thank You! For submission.</p>
            <p className="text-lg text-[#334988] font-sans">
              Our Team will connect with you shortly for Free Counselling!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadFormModal;
